import { BorderOutlined } from '@ant-design/icons'
import { CarouselProps, Space } from 'antd'
import { EditButton, TrashButton } from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { DefaultObservationImage } from 'pages/pointClouds/components/DefaultObservationImage'
import {
  observationTypeStyles,
  severityStyles,
} from 'pages/pointClouds/components/dropdowns/EnumStyles'
import { useObservationHandlers } from 'pages/pointClouds/components/observations/hooks/useObservationHandlers'
import { ObservationCarousel } from 'pages/pointClouds/components/shared/ObservationCarousel'
import {
  ObservationTypeTag,
  SeverityTag,
  TicketStatusTag,
} from 'pages/pointClouds/components/shared/ObservationTags'
import { useObservationImages } from 'pages/pointClouds/utils'
import React from 'react'
import styled from 'styled-components'
import { GeckoSiteObservation } from 'types'

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
`

const LabelValueRow = styled(Row)`
  display: grid;
  grid-template-columns: 65px 1fr;
`

const GridRow = styled(Row)`
  display: grid;
  grid-template-columns: 65px 1fr auto 1fr;
  gap: 8px;
  min-width: 0; /* Enable text truncation in grid */
`

const Label = styled.span`
  color: #888;
  font-size: 12px;
  white-space: nowrap; /* Prevent label wrapping */
`

const Dot = styled.span`
  margin: 0px;
  color: #888;
  &::before {
    content: '•';
  }
`

const Value = styled.span`
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0; /* Enable text truncation */
`

const HeaderValue = styled(Value)`
  font-size: 20px;
  font-weight: bold;
`

const RightContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// Restore ObservationCardWrapper
const ObservationCardWrapper = styled.div`
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.5rem; // Match CantileverCard border-radius
  border: 1px solid transparent; // Start transparent for smooth transition
  background-color: ${({ theme }) =>
    theme.forecast.cardBackground}; // Use theme background

  &:hover {
    background-color: rgba(60, 60, 60, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  // Prevent hover effect if CantileverCard itself is hovered (e.g., over buttons)
  > div:hover {
    // Reset specific hover styles applied by the wrapper
    transform: none;
    box-shadow: none;
    border-color: transparent; // Or match the base CantileverCard border
    background-color: ${({ theme }) => theme.forecast.cardBackground};
  }
`

interface ObservationCardProps {
  observation: GeckoSiteObservation
  onClick: () => void
}

export const ObservationCard: React.FC<ObservationCardProps> = ({
  observation,
  onClick,
}) => {
  const severity = observation.data?.severity as
    | keyof typeof severityStyles
    | undefined
  const observationType = observation.data?.type as
    | keyof typeof observationTypeStyles
    | undefined

  const carouselProps: CarouselProps = {
    autoplay: false,
    dotPlacement: 'bottom',
  }
  const carouselWrapperStyle: React.CSSProperties = {
    width: '100px',
    flexShrink: 0,
    aspectRatio: '1',
    backgroundColor: '#222',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative',
  }

  const imageStyle: React.CSSProperties = {
    objectFit: 'cover',
    aspectRatio: '1',
  }

  const { handleEditObservation, handleDeleteObservation } =
    useObservationHandlers()

  const images = useObservationImages(observation.id)
  return (
    <ObservationCardWrapper
      onClick={(e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClick()
        }
      }}>
      <CantileverCard>
        <CantileverCard.CardHeader
          leadingContent={
            <Space>
              {severity && severityStyles[severity] && (
                <>
                  <IconWrapper>
                    {React.cloneElement(
                      severityStyles[severity].icon as React.ReactElement<
                        React.HTMLAttributes<HTMLElement>
                      >,
                      {
                        style: { color: severityStyles[severity].color },
                      }
                    )}
                  </IconWrapper>
                  <SeverityTag severity={severity} />
                </>
              )}
              {observationType && (
                <>
                  <IconWrapper>
                    {observationTypeStyles[observationType]?.icon ?? (
                      <BorderOutlined />
                    )}
                  </IconWrapper>
                  <ObservationTypeTag type={observationType} />
                </>
              )}
            </Space>
          }
          trailingContent={<TicketStatusTag status={observation.data.status} />}
        />

        <CantileverCard.Section topPadding>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
            }}
            onClick={(e) => {
              // Prevent card click when clicking content
              e.stopPropagation()
              onClick()
            }}>
            <ObservationCarousel
              images={images}
              carouselProps={carouselProps}
              defaultImageComponent={
                <DefaultObservationImage observation={observation} />
              }
              style={carouselWrapperStyle}
              imageStyle={imageStyle}
            />
            <RightContent>
              <Row>
                <HeaderValue>
                  {observation.data?.equipment || 'N/A'}
                </HeaderValue>
              </Row>

              <Row>
                <Value>{observation.data?.area_id || 'N/A'}</Value>
                <Dot />
                <Value>{observation.data?.component || 'N/A'}</Value>
              </Row>

              <LabelValueRow>
                <Label>Fault</Label>
                <Value>{observation.data?.name || 'N/A'}</Value>
              </LabelValueRow>

              <LabelValueRow>
                <Label>Notes</Label>
                <Value>
                  {observation.data?.description || 'No description'}
                </Value>
              </LabelValueRow>

              <GridRow>
                <Label>Reporter</Label>
                <Value>{observation.data?.department || 'PdM'}</Value>
                <Label>Date</Label>
                <Value>{observation.data?.date_found || 'N/A'}</Value>
              </GridRow>
            </RightContent>
          </div>
        </CantileverCard.Section>

        <CantileverCard.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <EditButton
                onClick={(e) => {
                  e.stopPropagation()
                  console.warn('Editing observation', observation.id)
                  handleEditObservation(observation)
                }}
                title='Edit'
                ghost
              />
              <TrashButton
                onClick={(e) => {
                  e.stopPropagation()
                  if (
                    window.confirm(
                      'Are you sure you want to delete this observation?'
                    )
                  ) {
                    handleDeleteObservation(observation.id)
                  }
                }}
                title='Delete'
                ghost
              />
            </Space>
          </div>
        </CantileverCard.Section>
      </CantileverCard>
    </ObservationCardWrapper>
  )
}
